import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  CheckCircle2, Circle, Send, Plus, Pencil, Trash2,
  ClipboardCheck, Settings, History, Loader2, GripVertical, Bell, Mail,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const ROLE_LABELS = {
  admin: 'Admin',
  manager: 'Manager',
  auditor: 'Auditor',
};

// Schedule pattern dropdown options. Keep in sync with VALID_SCHEDULES
// on the backend (routes/my_work_router.py).
const SCHEDULE_OPTIONS = [
  { value: 'daily',          label: 'Every day' },
  { value: 'weekdays',       label: 'Weekdays only (Mon–Fri)' },
  { value: 'weekends',       label: 'Weekends only (Sat–Sun)' },
  { value: 'weekly:mon',     label: 'Mondays only' },
  { value: 'weekly:tue',     label: 'Tuesdays only' },
  { value: 'weekly:wed',     label: 'Wednesdays only' },
  { value: 'weekly:thu',     label: 'Thursdays only' },
  { value: 'weekly:fri',     label: 'Fridays only' },
  { value: 'weekly:sat',     label: 'Saturdays only' },
  { value: 'weekly:sun',     label: 'Sundays only' },
  { value: 'monthly_first',  label: '1st of every month' },
  { value: 'monthly_last',   label: 'Last day of every month' },
];

// Compact row badge for non-daily schedules.
const SCHEDULE_BADGE = {
  weekdays:       'MON–FRI',
  weekends:       'SAT–SUN',
  'weekly:mon':   'MON',
  'weekly:tue':   'TUE',
  'weekly:wed':   'WED',
  'weekly:thu':   'THU',
  'weekly:fri':   'FRI',
  'weekly:sat':   'SAT',
  'weekly:sun':   'SUN',
  monthly_first:  '1ST',
  monthly_last:   'EOM',
};

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700 border-purple-300',
  manager: 'bg-blue-100 text-blue-700 border-blue-300',
  auditor: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

const AdminMyWork = () => {
  const [tab, setTab] = useState('today'); // today | configure | history
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [togglingTaskId, setTogglingTaskId] = useState(null);

  // Admin-only state
  const [configRole, setConfigRole] = useState('auditor');
  const [configTasks, setConfigTasks] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [taskModal, setTaskModal] = useState(null); // {id?, role, title, description}

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Current user role from token (decoded by /today response)
  const currentRole = todayData?.role || 'admin';
  const isAdminUser = currentRole === 'admin';

  // ===== Today =====

  const fetchToday = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/my-work/today`, getAuthHeader());
      setTodayData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tasks');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchToday(); }, []);

  const toggleTask = async (taskId, newCompleted) => {
    if (todayData?.submitted) return;
    setTogglingTaskId(taskId);
    // Optimistic UI
    setTodayData(d => ({
      ...d,
      tasks: d.tasks.map(t =>
        t.id === taskId ? { ...t, completed: newCompleted } : t,
      ),
    }));
    try {
      await axios.post(
        `${API}/admin/my-work/tasks/${taskId}/toggle`,
        { completed: newCompleted },
        getAuthHeader(),
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update task');
      fetchToday(); // revert
    } finally { setTogglingTaskId(null); }
  };

  const handleSubmit = async () => {
    if (!todayData) return;
    const total = todayData.tasks.length;
    const done = todayData.tasks.filter(t => t.completed).length;
    const undone = total - done;
    if (undone > 0) {
      const ok = window.confirm(
        `You have ${undone} unchecked task(s). Submit anyway?\n\n` +
        `This will lock today's list, notify the admin, and email a summary.`,
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/my-work/submit`, {}, getAuthHeader());
      toast.success(`Shift submitted (${done}/${total} tasks completed)`);
      fetchToday();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  // ===== Configure (admin-only) =====

  const fetchConfigTasks = async (role) => {
    setConfigLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/my-work/admin/tasks?role=${role}`,
        getAuthHeader(),
      );
      setConfigTasks(res.data.tasks || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load config');
    } finally { setConfigLoading(false); }
  };

  useEffect(() => {
    if (tab === 'configure' && isAdminUser) fetchConfigTasks(configRole);
  }, [tab, configRole, isAdminUser]);

  const handleSaveTask = async () => {
    if (!taskModal || !taskModal.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      if (taskModal.id) {
        await axios.put(
          `${API}/admin/my-work/admin/tasks/${taskModal.id}`,
          {
            title: taskModal.title.trim(),
            description: (taskModal.description || '').trim(),
            // Only send recurring when it changed from the original to
            // avoid no-op writes (purely defensive — backend handles
            // duplicates fine).
            ...(taskModal.recurring !== taskModal.original_recurring
              ? { recurring: taskModal.recurring !== false }
              : {}),
            ...(taskModal.schedule_pattern !== taskModal.original_schedule_pattern
              ? { schedule_pattern: taskModal.schedule_pattern || 'daily' }
              : {}),
            ...(taskModal.reminder_enabled !== taskModal.original_reminder_enabled
              ? { reminder_enabled: taskModal.reminder_enabled === true }
              : {}),
            // Only send role when it changed from the original — the
            // backend treats a role change as a "move to end of new
            // list" operation, which we don't want to trigger on
            // a no-op edit.
            ...(taskModal.role && taskModal.role !== taskModal.original_role
              ? { role: taskModal.role }
              : {}),
          },
          getAuthHeader(),
        );
        const roleChanged = taskModal.role && taskModal.role !== taskModal.original_role;
        toast.success(
          roleChanged
            ? `Task moved to ${ROLE_LABELS[taskModal.role]} list`
            : 'Task updated',
        );
        // If the role changed, the task is no longer in the currently-
        // viewed list — refetch the destination role so the admin sees
        // their change land somewhere visible.
        if (roleChanged) setConfigRole(taskModal.role);
      } else {
        await axios.post(
          `${API}/admin/my-work/admin/tasks`,
          {
            role: taskModal.role,
            title: taskModal.title.trim(),
            description: (taskModal.description || '').trim(),
            recurring: taskModal.recurring !== false,
            schedule_pattern: taskModal.schedule_pattern || 'daily',
            reminder_enabled: taskModal.reminder_enabled === true,
          },
          getAuthHeader(),
        );
        toast.success('Task added');
      }
      setTaskModal(null);
      fetchConfigTasks(taskModal.role && taskModal.role !== taskModal.original_role
        ? taskModal.role
        : configRole);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task from the role list?')) return;
    try {
      await axios.delete(`${API}/admin/my-work/admin/tasks/${taskId}`, getAuthHeader());
      toast.success('Task deleted');
      fetchConfigTasks(configRole);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // Inline checkbox toggle on the row — flips the `recurring` flag on
  // the task and persists immediately. Optimistic update for snappy
  // feel; reverts on backend error.
  const handleToggleRecurring = async (taskId, nextValue) => {
    setConfigTasks(prev =>
      prev.map(t => (t.id === taskId ? { ...t, recurring: nextValue } : t))
    );
    try {
      await axios.put(
        `${API}/admin/my-work/admin/tasks/${taskId}`,
        { recurring: nextValue },
        getAuthHeader(),
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update — reverting');
      // Re-fetch to snap back to server truth
      fetchConfigTasks(configRole);
    }
  };

  // ----- Drag-and-drop reorder (native HTML5 DnD, no extra deps) -----
  // Optimistically reorder in local state on drop, then POST the new
  // ordering to the backend. If the backend rejects we re-fetch to
  // snap back to truth.
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (idx) => (e) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires data to be set on the dataTransfer or the drag
    // won't initialize. Value itself is ignored — we use React state.
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch {}
  };

  const handleDragOver = (idx) => (e) => {
    e.preventDefault();  // required to allow drop
    if (dragIndex === null || dragIndex === idx) return;
    if (dragOverIndex !== idx) setDragOverIndex(idx);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (idx) => async (e) => {
    e.preventDefault();
    const from = dragIndex;
    setDragIndex(null);
    setDragOverIndex(null);
    if (from === null || from === idx) return;

    // Compute the new order client-side
    const updated = [...configTasks];
    const [moved] = updated.splice(from, 1);
    updated.splice(idx, 0, moved);
    setConfigTasks(updated);

    // Persist to the backend
    try {
      await axios.post(
        `${API}/admin/my-work/admin/tasks/reorder`,
        {
          role: configRole,
          ordered_task_ids: updated.map(t => t.id),
        },
        getAuthHeader(),
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reorder failed — reverting');
      fetchConfigTasks(configRole);
    }
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // ===== History =====

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/my-work/admin/submissions?days=14`,
        getAuthHeader(),
      );
      setHistory(res.data.submissions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load history');
    } finally { setHistoryLoading(false); }
  };

  useEffect(() => {
    if (tab === 'history' && isAdminUser) fetchHistory();
  }, [tab, isAdminUser]);

  // ===== Render =====

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-500" size={32} />
      </div>
    );
  }

  const totalTasks = todayData?.tasks?.length || 0;
  const doneTasks = todayData?.tasks?.filter(t => t.completed).length || 0;
  const pct = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : 0;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-my-work">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Work</h1>
          <p className="text-sm text-gray-500 mt-1">
            Daily checklist for your role. Resets every morning.
          </p>
        </div>
        <Badge className={`text-sm py-1.5 px-3 border ${ROLE_COLORS[currentRole] || ROLE_COLORS.admin}`}>
          {ROLE_LABELS[currentRole] || currentRole}
        </Badge>
      </div>

      <div className="flex gap-2 mt-6 mb-4 border-b">
        {[
          { key: 'today', icon: ClipboardCheck, label: "Today's tasks" },
          ...(isAdminUser ? [
            { key: 'configure', icon: Settings, label: 'Configure tasks' },
            { key: 'history', icon: History, label: 'History' },
          ] : []),
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-purple-600 text-purple-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
            data-testid={`mywork-tab-${t.key}`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* ========== TODAY ========== */}
      {tab === 'today' && todayData && (
        <div>
          {todayData.submitted ? (
            <Card className="p-5 mb-4 bg-emerald-50 border-emerald-200">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-emerald-600" size={28} />
                <div>
                  <p className="font-semibold text-emerald-900">Shift submitted</p>
                  <p className="text-xs text-emerald-700">
                    Submitted at {new Date(todayData.submitted_at).toLocaleString()} ·
                    Tomorrow's list will reset automatically.
                  </p>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="p-5 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">{todayData.shift_date}</span>
                <span className="text-sm font-bold text-gray-900">
                  {doneTasks}/{totalTasks} · {pct}%
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </Card>
          )}

          <div className="space-y-2">
            {todayData.tasks.length === 0 && (
              <Card className="p-8 text-center text-gray-500">
                No tasks configured for your role yet.
              </Card>
            )}
            {todayData.tasks.map(t => (
              <Card
                key={t.id}
                className={`p-4 transition-colors ${
                  t.completed ? 'bg-emerald-50 border-emerald-200' : 'bg-white'
                }`}
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => toggleTask(t.id, !t.completed)}
                    disabled={todayData.submitted || togglingTaskId === t.id}
                    className="mt-0.5 flex-shrink-0 disabled:opacity-50"
                    data-testid={`task-toggle-${t.id}`}
                    aria-label={t.completed ? 'Uncheck task' : 'Check task'}
                  >
                    {t.completed ? (
                      <CheckCircle2 className="text-emerald-600" size={22} />
                    ) : (
                      <Circle className="text-gray-400 hover:text-purple-500" size={22} />
                    )}
                  </button>
                  <div className="flex-1">
                    <p
                      className={`font-medium ${
                        t.completed ? 'text-emerald-900 line-through' : 'text-gray-900'
                      }`}
                    >
                      {t.title}
                    </p>
                    {t.description && (
                      <p className="text-sm text-gray-500 mt-1">{t.description}</p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {!todayData.submitted && totalTasks > 0 && (
            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-purple-600 hover:bg-purple-500"
                data-testid="mywork-submit-shift"
              >
                {submitting
                  ? <Loader2 className="animate-spin mr-2" size={16} />
                  : <Send className="mr-2" size={16} />
                }
                Submit Shift
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ========== CONFIGURE (admin only) ========== */}
      {tab === 'configure' && isAdminUser && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            {['auditor', 'manager', 'admin'].map(r => (
              <button
                key={r}
                onClick={() => setConfigRole(r)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  configRole === r
                    ? ROLE_COLORS[r]
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`config-role-${r}`}
              >
                {ROLE_LABELS[r]} list
              </button>
            ))}
            <div className="flex-1" />
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm("Send today's reminder emails now to everyone in this role list with `Email me a morning reminder` enabled?")) return;
                try {
                  const res = await axios.post(
                    `${API}/admin/my-work/admin/send-reminders-now`,
                    {},
                    getAuthHeader(),
                  );
                  const sent = res.data?.sent_role_users || 0;
                  toast.success(
                    sent === 0
                      ? 'No matching tasks found for today.'
                      : `Sent ${sent} reminder email${sent === 1 ? '' : 's'}.`
                  );
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Send failed');
                }
              }}
              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
              title="Force-send today's morning digest to every role user"
              data-testid="send-reminders-now-btn"
            >
              <Mail size={14} className="mr-1" /> Send reminders now
            </Button>
            <Button
              onClick={() => setTaskModal({ role: configRole, title: '', description: '', recurring: true, schedule_pattern: 'daily', reminder_enabled: false })}
              className="bg-purple-600 hover:bg-purple-500"
              data-testid="add-task-btn"
            >
              <Plus size={16} className="mr-1" /> Add task
            </Button>
          </div>

          {configLoading ? (
            <div className="p-12 flex justify-center">
              <Loader2 className="animate-spin text-purple-500" size={24} />
            </div>
          ) : (
            <div className="space-y-2">
              {configTasks.length === 0 && (
                <Card className="p-8 text-center text-gray-500">
                  No tasks for this role yet — add one above.
                </Card>
              )}
              {configTasks.length > 1 && (
                <p className="text-xs text-gray-400 px-1 mb-1 flex items-center gap-1">
                  <GripVertical size={11} className="text-gray-300" />
                  Drag the grip on the left to reorder tasks
                </p>
              )}
              {configTasks.map((t, idx) => (
                <Card
                  key={t.id}
                  draggable
                  onDragStart={handleDragStart(idx)}
                  onDragOver={handleDragOver(idx)}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop(idx)}
                  onDragEnd={handleDragEnd}
                  className={`p-4 flex items-start gap-3 transition-all ${
                    dragIndex === idx ? 'opacity-40' : ''
                  } ${
                    dragOverIndex === idx
                      ? 'ring-2 ring-purple-400 border-purple-300 -translate-y-px shadow-md'
                      : ''
                  }`}
                  data-testid={`task-row-${t.id}`}
                >
                  {/* Drag handle — Cursor stays grab-able only on the
                      grip, so the rest of the card still receives
                      normal clicks for the edit/delete buttons. */}
                  <div
                    className="text-gray-300 hover:text-purple-500 cursor-grab active:cursor-grabbing pt-1 select-none"
                    title="Drag to reorder"
                    data-testid={`drag-handle-${t.id}`}
                  >
                    <GripVertical size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-gray-900">{t.title}</p>
                      {t.is_default && (
                        <Badge className="bg-gray-100 text-gray-600 text-[10px] uppercase tracking-wide">
                          Default
                        </Badge>
                      )}
                      {t.recurring !== false && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px] uppercase tracking-wide notranslate">
                          Daily
                        </Badge>
                      )}
                      {SCHEDULE_BADGE[t.schedule_pattern] && (
                        <Badge
                          className="bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wide notranslate"
                          title="Custom schedule pattern"
                        >
                          {SCHEDULE_BADGE[t.schedule_pattern]}
                        </Badge>
                      )}
                      {t.reminder_enabled && (
                        <Badge
                          className="bg-indigo-100 text-indigo-700 text-[10px] inline-flex items-center gap-0.5"
                          title="Morning email reminder enabled"
                        >
                          <Bell size={10} /> Reminder
                        </Badge>
                      )}
                    </div>
                    {t.description && (
                      <p className="text-sm text-gray-500 mt-1">{t.description}</p>
                    )}
                    {/* Inline recurring checkbox — same control the user
                        pointed at in the screenshot. Clicking instantly
                        persists; label changes to clarify the semantics. */}
                    <label
                      className="mt-2 inline-flex items-center gap-2 cursor-pointer text-xs select-none"
                      data-testid={`recurring-row-${t.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={t.recurring !== false}
                        onChange={(e) => handleToggleRecurring(t.id, e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-400 cursor-pointer"
                        data-testid={`recurring-checkbox-${t.id}`}
                      />
                      <span className={`${
                        t.recurring !== false ? 'text-emerald-700 font-medium' : 'text-gray-500'
                      }`}>
                        {t.recurring !== false
                          ? 'Repeats daily automatically'
                          : 'One-time — disappears after first completion'}
                      </span>
                    </label>
                    <p className="text-xs text-gray-400 mt-1">
                      Added by {t.created_by_name || t.created_by_id || 'unknown'}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setTaskModal({
                        id: t.id,
                        role: t.role,
                        original_role: t.role,
                        title: t.title,
                        description: t.description || '',
                        recurring: t.recurring !== false,
                        original_recurring: t.recurring !== false,
                        schedule_pattern: t.schedule_pattern || 'daily',
                        original_schedule_pattern: t.schedule_pattern || 'daily',
                        reminder_enabled: t.reminder_enabled === true,
                        original_reminder_enabled: t.reminder_enabled === true,
                      })}
                      data-testid={`edit-task-${t.id}`}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteTask(t.id)}
                      className="text-red-500 border-red-200 hover:bg-red-50"
                      data-testid={`delete-task-${t.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== HISTORY (admin only) ========== */}
      {tab === 'history' && isAdminUser && (
        <div>
          {historyLoading ? (
            <div className="p-12 flex justify-center">
              <Loader2 className="animate-spin text-purple-500" size={24} />
            </div>
          ) : history.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              No shift submissions in the last 14 days yet.
            </Card>
          ) : (
            <div className="space-y-2">
              {history.map(s => (
                <Card key={s.id} className="p-4 flex items-center gap-4">
                  <Badge className={`text-xs py-1 px-2 border ${ROLE_COLORS[s.role]}`}>
                    {ROLE_LABELS[s.role] || s.role}
                  </Badge>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{s.user_name}</p>
                    <p className="text-xs text-gray-500">
                      {s.shift_date} · submitted {new Date(s.submitted_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${
                      s.completed_tasks === s.total_tasks
                        ? 'text-emerald-600'
                        : 'text-amber-600'
                    }`}>
                      {s.completed_tasks}/{s.total_tasks}
                    </p>
                    <p className="text-xs text-gray-400">tasks done</p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== Add / Edit Task Modal ========== */}
      <Dialog open={!!taskModal} onOpenChange={(open) => !open && setTaskModal(null)}>
        <DialogContent data-testid="task-modal">
          <DialogHeader>
            <DialogTitle>
              {taskModal?.id ? 'Edit Task' : `Add Task for ${ROLE_LABELS[taskModal?.role]}`}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              {taskModal?.id
                ? 'Update task details, schedule, or reminder settings.'
                : 'Add a new task to this role’s daily checklist.'}
            </DialogDescription>
          </DialogHeader>
          {taskModal && (
            <div className="space-y-3">
              {taskModal.id && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Role list
                  </label>
                  <select
                    value={taskModal.role}
                    onChange={(e) => setTaskModal({ ...taskModal, role: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                    data-testid="task-modal-role"
                  >
                    {Object.entries(ROLE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                  {taskModal.role !== taskModal.original_role && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                      Will move from <b className="mx-1">{ROLE_LABELS[taskModal.original_role]}</b>
                      to <b className="mx-1">{ROLE_LABELS[taskModal.role]}</b> on save.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <Input
                  value={taskModal.title}
                  onChange={(e) => setTaskModal({ ...taskModal, title: e.target.value })}
                  placeholder="e.g. Review pending audit sessions"
                  maxLength={300}
                  data-testid="task-modal-title"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Description <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <Textarea
                  value={taskModal.description || ''}
                  onChange={(e) => setTaskModal({ ...taskModal, description: e.target.value })}
                  placeholder="What does the worker need to know to complete this?"
                  rows={3}
                  maxLength={1000}
                  data-testid="task-modal-description"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Show on
                </label>
                <select
                  value={taskModal.schedule_pattern || 'daily'}
                  onChange={(e) => setTaskModal({ ...taskModal, schedule_pattern: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="task-modal-schedule"
                >
                  {SCHEDULE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Controls which calendar days this task is eligible to appear on the worker's list.
                </p>
              </div>
              <label
                className="flex items-start gap-2 cursor-pointer p-3 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 select-none"
                data-testid="task-modal-recurring-row"
              >
                <input
                  type="checkbox"
                  checked={taskModal.recurring !== false}
                  onChange={(e) => setTaskModal({ ...taskModal, recurring: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-400"
                  data-testid="task-modal-recurring"
                />
                <div className="text-sm">
                  <div className="font-medium text-gray-800">Repeats daily automatically</div>
                  <div className="text-xs text-gray-500">
                    Checked: task appears every shift (e.g. <i>Clock in for work</i>).
                    Unchecked: one-time task — disappears after first completion.
                  </div>
                </div>
              </label>
              <label
                className="flex items-start gap-2 cursor-pointer p-3 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 select-none"
                data-testid="task-modal-reminder-row"
              >
                <input
                  type="checkbox"
                  checked={taskModal.reminder_enabled === true}
                  onChange={(e) => setTaskModal({ ...taskModal, reminder_enabled: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-400"
                  data-testid="task-modal-reminder"
                />
                <div className="text-sm">
                  <div className="font-medium text-gray-800 flex items-center gap-1.5">
                    <Bell size={13} className="text-indigo-600" />
                    Email me a morning reminder
                  </div>
                  <div className="text-xs text-gray-500">
                    Every user with this role receives a digest email at 8 AM CST on
                    days this task is eligible. Useful for monthly compliance, payroll,
                    or weekly meeting prep.
                  </div>
                </div>
              </label>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setTaskModal(null)}>Cancel</Button>
                <Button
                  onClick={handleSaveTask}
                  className="bg-purple-600 hover:bg-purple-500"
                  data-testid="task-modal-save"
                >
                  {taskModal.id ? 'Save' : 'Add'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMyWork;
